/**
 * TargetsTableSection - Table card with pagination and sortable target rows
 */
import React from 'react';
import {
  Button,
  Card,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import { cn, formatVesselName } from '@ais-ui/utils';
import { Messages } from '@ais-ui/locale-management';
import {
  PiCaretDown,
  PiCaretUp,
  PiCaretUpDown,
  PiDotsThreeVerticalBold,
} from 'react-icons/pi';
import { TargetFromTargetList } from '@ais-ui/websockets';
import { DensityClasses } from './shared/popupDensityClasses';

// --- SortableHead (local to this file) ---

interface SortableHeadProps {
  column: string;
  orderBy: string;
  orderAscending: boolean;
  onSort: (column: string) => void;
  densityClasses: DensityClasses;
  children: React.ReactNode;
}

const SortableHead: React.FC<SortableHeadProps> = ({
  column,
  orderBy,
  orderAscending,
  onSort,
  densityClasses,
  children,
}) => (
  <TableHead
    className={cn('cursor-pointer select-none', densityClasses.tableHeader)}
    onClick={() => onSort(column)}
  >
    <div className="flex items-center justify-between">
      <span className={densityClasses.text}>{children}</span>
      {orderBy === column ? (
        orderAscending ? (
          <PiCaretUp className={densityClasses.icon.small} />
        ) : (
          <PiCaretDown className={densityClasses.icon.small} />
        )
      ) : (
        <PiCaretUpDown
          className={cn(densityClasses.icon.small, 'opacity-50')}
        />
      )}
    </div>
  </TableHead>
);

// --- TargetsTableSection ---

export interface TargetsTableSectionProps {
  targets: TargetFromTargetList[];
  totalCount: number;
  page: number;
  totalPages: number;
  perPage: number;
  orderBy: string;
  orderAscending: boolean;
  onSort: (column: string) => void;
  onPageChange: (page: number) => void;
  onViewTarget: (target: TargetFromTargetList) => void;
  densityClasses: DensityClasses;
  messages: Messages;
}

export const TargetsTableSection: React.FC<TargetsTableSectionProps> = ({
  targets,
  totalCount,
  page,
  totalPages,
  perPage,
  orderBy,
  orderAscending,
  onSort,
  onPageChange,
  onViewTarget,
  densityClasses,
  messages,
}) => {
  const sortableProps = { orderBy, orderAscending, onSort, densityClasses };

  return (
    <Card className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b">
        <span className="text-sm text-muted-foreground">
          {totalCount}{' '}
          {messages.popupTargetsRows?.replace('{count}', '') || 'rows'}
        </span>
        <div className="flex items-center gap-4">
          <span className="text-sm">
            {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              {messages.popupGenericPrevious}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              {messages.popupGenericNext}
            </Button>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <Table container={false}>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead className={cn('w-10', densityClasses.tableHeader)}>
                #
              </TableHead>
              <SortableHead column="name" {...sortableProps}>
                {messages.popupTargetsTableName}
              </SortableHead>
              <SortableHead column="mmsi" {...sortableProps}>
                {messages.popupTargetsTableMmsi}
              </SortableHead>
              <SortableHead column="imo" {...sortableProps}>
                {messages.popupTargetsTableImo}
              </SortableHead>
              <SortableHead column="callsign" {...sortableProps}>
                {messages.popupTargetsTableCallsign}
              </SortableHead>
              <SortableHead column="shiptype" {...sortableProps}>
                {messages.popupTargetsTableShipType}
              </SortableHead>
              <SortableHead column="country" {...sortableProps}>
                {messages.popupTargetsTableCountry}
              </SortableHead>
              <TableHead className={densityClasses.tableHeader}>
                {messages.popupTargetsTablePosition}
              </TableHead>
              <SortableHead column="sog" {...sortableProps}>
                {messages.popupTargetsTableSog}
              </SortableHead>
              <TableHead className={densityClasses.tableHeader}></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {targets.map((target, i) => (
              <TableRow key={`${target.mmsi}-${i}`} className="odd:bg-muted/50">
                <TableCell className={densityClasses.tableCell}>
                  {(page - 1) * perPage + i + 1}
                </TableCell>
                <TableCell
                  className={cn(
                    densityClasses.tableCell,
                    'font-medium max-w-32 truncate',
                  )}
                >
                  {formatVesselName(target.name)}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {target.mmsi}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {target.imo}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {target.callSign}
                </TableCell>
                <TableCell
                  className={cn(densityClasses.tableCell, 'max-w-24 truncate')}
                >
                  {target.shipType}
                </TableCell>
                <TableCell
                  className={cn(densityClasses.tableCell, 'max-w-24 truncate')}
                >
                  {target.country}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {target.lat?.toFixed(4)}, {target.lon?.toFixed(4)}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {target.sog && `${target.sog} kn`}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className={densityClasses.button}>
                        <PiDotsThreeVerticalBold
                          className={densityClasses.icon.medium}
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewTarget(target)}>
                        {messages.popupTargetsMenuViewTarget}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </Card>
  );
};
